import React, { useState, useEffect } from "react";
import {
  LoadingOverlay,
  Stack,
  Group,
  Title,
  Text,
  Button,
  ActionIcon,
} from "@mantine/core";
import { IconTrash, IconChevronRight, IconPlus } from "@tabler/icons-react";
import "./AddServicesPage.css";
import ServiceFormWizard from "./forms/ServiceFormWizard";
import { useApp } from "../context/AppContext";
import { useMediaQuery } from "@mantine/hooks";
import { calculateTotalDuration } from "../utils/timeUtils";

function AddServicesPage({ initialServices = null, onSubmit }) {
  const { deleteSalonService } = useApp();
  const [services, setServices] = useState(initialServices || []);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceTypeOptions, setServiceTypeOptions] = useState(null);
  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    if (initialServices != null) {
      setServices(initialServices);
      setIsLoading(false);
    }
  }, [initialServices]);

  useEffect(() => {
    setServiceTypeOptions([
      ...new Set(services.map((service) => service.service_type)),
    ]);
  }, [services]);

  const handleAddService = (serviceData) => {
    setServices([...services, serviceData]);
    setIsFormOpen(false);
  };

  const handleEditService = (serviceData) => {
    setServices(
      services.map((service) =>
        service.id === selectedService.id
          ? { ...service, ...serviceData }
          : service,
      ),
    );
    setIsEditFormOpen(false);
    setSelectedService(null);
  };

  const handleServiceClick = (service) => {
    setSelectedService(service);
    setIsEditFormOpen(true);
  };

  const handleDeleteService = async (id, e) => {
    e.stopPropagation();
    setIsLoading(true);

    const result = await deleteSalonService(id);
    if (result) {
      setServices(services.filter((service) => service.id !== id));
      setIsLoading(false);
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const separator = hours && mins ? " " : "";
    return `${hours ? `${hours}hr` : ""}${separator}${mins ? `${mins}min` : ""}`;
  };

  return (
    <Stack>
      <LoadingOverlay visible={isLoading} />
      <Stack
        p={{ base: "0", sm: "md", md: "xl" }}
        w={isMobile ? "100%" : "90%"}
        maw="60rem"
        mx="auto"
        align="center"
      >
        <Title order={2} c="gray.8">
          Add Services
        </Title>

        <Stack w="100%" mb="xl" gap="0">
          {services.map((service) => (
            <Group
              p="lg"
              key={service.id}
              justify="space-between"
              onClick={() => handleServiceClick(service)}
              className="service-row"
              style={{ cursor: "pointer", borderBottom: "1px solid #e8e8e8" }}
            >
              <Group>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={(e) => handleDeleteService(service.id, e)}
                  size={isMobile ? "sm" : "lg"}
                  radius="xl"
                  aria-label="Delete service"
                >
                  <IconTrash />
                </ActionIcon>
                <Text size={isMobile ? "sm" : "md"} c="gray.8">
                  {service.name}
                </Text>
              </Group>

              <Group>
                <Text size={isMobile ? "sm" : "md"} c="gray.6">
                  {formatDuration(calculateTotalDuration(service))} | $
                  {service.default_price}
                </Text>
                <IconChevronRight size={isMobile ? 16 : 20} />
              </Group>
            </Group>
          ))}

          <Button
            onClick={() => setIsFormOpen(true)}
            leftSection={<IconPlus size={isMobile ? 16 : 20} />}
            variant="subtle"
            size={isMobile ? "sm" : "md"}
            radius="xl"
            color="teal"
            py="sm"
            h="fit-content"
          >
            Add Services
          </Button>
        </Stack>

        <Stack w="100%" px={isMobile ? "md" : "xl"}>
          <Button
            onClick={() => onSubmit(services, true)}
            fullWidth
            size={isMobile ? "sm" : "md"}
            color="teal"
          >
            Continue
          </Button>
          <Button
            variant="light"
            onClick={() => onSubmit(services, true)}
            fullWidth
            size={isMobile ? "sm" : "md"}
            color="gray"
          >
            Skip for Now
          </Button>
        </Stack>

        <ServiceFormWizard
          opened={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSuccess={handleAddService}
          serviceTypes={serviceTypeOptions}
        />

        <ServiceFormWizard
          opened={isEditFormOpen}
          onClose={() => {
            setIsEditFormOpen(false);
            setSelectedService(null);
          }}
          onSuccess={handleEditService}
          initialData={selectedService}
          isEditing={true}
          serviceTypes={serviceTypeOptions}
        />
      </Stack>
    </Stack>
  );
}

export default AddServicesPage;
